import React, { useState, useEffect,useCallback } from 'react';
import axios from '../../../../lib/axios';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { Box, Chip } from '@mui/material';
import FilterTable from '../../../../atoms/TableComponent/FilterTable/FilterTable';

export default function WorkerTypeTable() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const [tappedRowId, setTappedRowId] = useState(null);
  const [lastTap, setLastTap] = useState(0);
  const DOUBLE_TAP_DELAY = 300;
  const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const permissions = usePermissions("Worker Type Master");
  const { AddRec = "0" } = permissions || {};

  useEffect(() => {
    fetchWorkerTypeData();
  }, []);

  const handleCellClicked = useCallback(
    (params) => {
      if (!params.data?.HkWrkTypeId) return;

      const id = params.data.HkWrkTypeId;

      // visual feedback
      setTappedRowId(id);
      setTimeout(() => setTappedRowId(null), 300);

      const now = Date.now();
      const timeSince = now - lastTap;

      // Mobile → single tap
      if (isMobile()) {
        navigate('/worker-type', { state: { HkWrkTypeId: id, mode: 'view' } });
        return;
      }

      // Desktop → double-click (fallback for onRowDoubleClicked)
      if (timeSince > 0 && timeSince < DOUBLE_TAP_DELAY) {
        navigate('/worker-type', { state: { HkWrkTypeId: id, mode: 'view' } });
      }
      setLastTap(now);
    },
    [navigate, lastTap]
  );

  const getRowStyle = (params) => {
    if (tappedRowId === params.data?.HkWrkTypeId) {
      return { background: '#bbdefb', transition: 'background 0.2s' };
    }
    return null;
  };

  const fetchWorkerTypeData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('MstHkWrkType/GetMstHkWrkTypeDashBoard', {
        "start": 0,
        "PageSize": 0,
        "SearchText": ""
      });
      const { data: { Status, Data, Message } } = response;
      if (Status === 0 && Array.isArray(Data)) {
        const formattedData = Data.map(hkWrkType => ({
          ...hkWrkType,
          RowNum: hkWrkType.RowNum || '',
          HkTypeName: hkWrkType.HkTypeName || '',
          Abrv: hkWrkType.Abrv || '',
          Remark: hkWrkType.Remark || '',
          Status: hkWrkType.Status === null ? '' : (hkWrkType.Status == "1" ? 'Active' : 'Inactive'),
        }));
        setRows(formattedData);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching housekeeping type data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const handleClick = () => {
    navigate('/worker-type', { state: { mode: "add" } });
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleLocationclick = () => {
    navigate('/masters/operations');
  };

  const handleRowDoubleClick = (params) => {
    const hkWrkTypeId = params?.data?.HkWrkTypeId;
    if (hkWrkTypeId) {
      navigate('/worker-type', { state: { HkWrkTypeId: hkWrkTypeId, mode: 'view' } });
    }
  };

  const TruncatedCellRenderer = (props) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const cellRef = React.useRef(null);

    useEffect(() => {
      if (cellRef.current) {
        const isTextOverflowed =
          cellRef.current.scrollWidth > cellRef.current.clientWidth ||
          (props.value && props.value.length > 20);
        setIsOverflowed(isTextOverflowed);
      }
    }, [props.value]);

    const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

    return (
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    );
  };

  const StatusCellRenderer = (props) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active':
          return '#4caf50';
        case 'InActive':
          return '#f44336';
        default:
          return '#757575';
      }
    };

    return (
      <Chip
        label={props.value}
        className="status-chip"
        sx={{
          backgroundColor: getStatusColor(props.value),
          color: 'white',
          width: '100%',
          maxWidth: '100px',
        }}
      />
    );
  };

  const columnDefs = [
    { headerName: 'Sr.No.', field: 'RowNum', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'WorkerTypeName', field: 'HkWrkTypeName', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Abrv', field: 'Abrv', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Remark', field: 'Remark', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Status', field: 'Status', width: 100, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  ];

  return (
    <Box sx={{ height: '90vh' }}>
      <FilterTable
        columnDefs={columnDefs}
        rowData={rows}
        onRowDoubleClicked={handleRowDoubleClick}
        isLoading={isLoading}
        rowSelection="single"
        onHomeClick={handleHomeClick}
        onParentClick={handleLocationclick}
        breadcrumbParentLabel="Operations"
        breadcrumbCurrentLabel="Worker Type Master"
        onAddClick={handlePermittedClick}
        hasAddPermission={AddRec === "1"}
        onCellClicked={handleCellClicked}
        getRowStyle={getRowStyle}
      />
    </Box>
  );
}

